import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { serviceGroups, serviceMeta } from '../content/services';
import { CHANNELS } from '../content/contacts';

/**
 * Підвал.
 *
 * Світлий. Побував темним, у пару до темної форми на головній, але власник
 * попросив прибрати контраст між світлим і темним узагалі — і форма, і
 * підвал повернулися на світле. Від старого білого підвалу лишилася
 * розкладка: чотири колонки й рядок із соцкнопками та копірайтом; зайві
 * анімації й декор (коло, крапка, риска, що малювалася дві секунди) пішли.
 *
 * Анімацій поменшало: риска, що малювалася дві секунди із затримкою в
 * секунду, тримала пререндер на зайві три секунди на кожній сторінці й
 * нічого не додавала. Лишилися чотири колонки, що проявляються по черзі.
 *
 * Перелік послуг — справжні посилання на сторінки послуг, з кожної сторінки
 * сайту: і людині є куди клікнути, і пошуковик бачить, що адреси пов'язані.
 */
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay }
});

const Footer = () => {
  const services = serviceMeta.map((m, i) => ({
    name: serviceGroups[i].title,
    to: `/poslugy/${m.slug}`
  }));

  // Усі пункти — окремі адреси: якорів на головну тут немає.
  const quickLinks = [
    { name: 'Про компанію', to: '/pro-kompaniyu' },
    { name: 'З чого почати', to: '/z-chogo-pochaty' },
    { name: 'Послуги', to: '/poslugy' },
    { name: 'План запуску клініки', to: '/plan-zapusku-kliniky' },
    { name: 'Цінова політика', to: '/tsinova-polityka' },
    { name: 'Блог', to: '/blog' },
    { name: 'Часті запитання', to: '/pytannya' },
    { name: 'Контакти', to: '/kontakty' }
  ];

  const heading = 'text-xs font-semibold uppercase tracking-[0.2em] text-slate-900 mb-5';
  const link = 'text-slate-600 hover:text-teal-800 transition-colors duration-200';

  return (
    <footer className="bg-white border-t border-slate-200">
      <div className="section-container">
        <div className="py-14 lg:py-20 grid grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-10 lg:gap-8">
          <motion.div className="col-span-2 lg:col-span-4" {...reveal(0)}>
            <div className="text-3xl font-black font-display tracking-tight leading-none">
              <span className="bg-gradient-to-r from-teal-600 to-sky-500 bg-clip-text text-transparent">
                VISIA
              </span>
            </div>
            <div className="mt-1.5 text-sm font-light uppercase tracking-[0.3em] text-slate-500">
              Medical Group
            </div>
            <span className="block w-12 h-px bg-teal-500/60 my-6" aria-hidden="true"></span>
            <p className="text-slate-600 leading-relaxed max-w-xs">
              Команда професійних юристів зі спеціалізацією на правовому супроводі медичних закладів та медичної практики.
            </p>
          </motion.div>

          <motion.div className="col-span-1 lg:col-span-3" {...reveal(0.1)}>
            <h2 className={heading}>Послуги</h2>
            <ul className="space-y-3 list-none">
              {services.map((service) => (
                <li key={service.to}>
                  <Link to={service.to} className={link}>
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="col-span-1 lg:col-span-2" {...reveal(0.2)}>
            <h2 className={heading}>Навігація</h2>
            <ul className="space-y-3 list-none">
              {quickLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={link}>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="col-span-2 lg:col-span-3" {...reveal(0.3)}>
            <h2 className={heading}>Контакти</h2>
            <ul className="space-y-3 list-none">
              {CHANNELS.map((channel) => (
                <li key={channel.key}>
                  <a href={channel.href} className={`inline-flex items-center gap-3 ${link}`}>
                    <Icon name={channel.icon} className="text-teal-700 text-sm flex-shrink-0" />
                    {/* Телефон в усіх трьох каналах той самий, тож повторювати
                        номер тричі немає сенсу — досить назви месенджера. */}
                    <span className="break-all">
                      {channel.key === 'phone' || channel.key === 'email' ? channel.label : channel.title}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="py-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex gap-3">
            {CHANNELS.map((channel) => (
              <a
                key={channel.key}
                href={channel.href}
                aria-label={channel.title}
                className="w-11 h-11 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-600 hover:text-teal-800 hover:border-teal-300 hover:bg-teal-50 transition-colors duration-300"
              >
                <Icon name={channel.icon} />
              </a>
            ))}
          </div>

          <div className="text-center md:text-right">
            <p className="text-slate-500 text-sm">&copy; 2026 Visia Medical Group</p>
            <p className="mt-1 text-slate-500 text-xs uppercase tracking-wider">
              Юридичний фундамент успіху вашого медичного бізнесу
            </p>
            {/* Ліцензія офіційних меж (карта «Де ми» на головній) вимагає
                згадати джерело; під самою картою підпису свідомо немає. */}
            <p className="mt-2 text-slate-500 text-xs">
              Картографічна основа: ДНВП «Картографія»,{' '}
              <a
                href="https://creativecommons.org/licenses/by/3.0/igo/deed.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-teal-800"
              >
                CC BY-IGO
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
